
import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Paper, Box, Typography, IconButton, Button} from 'views/components';
import {Translate} from 'views/module';
import {Edit} from 'views/icons';

import BiographyQuestion from './BiographyQuestion';

const BiographyQuestionGroup = (props) => {
  const [mode, setMode] = React.useState('VIEW');

  const [mouseOver, setMouseOver] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const [newBioQuestions, setNewBioQuestions] = React.useState([]);
  const [touchedBioQuestions, setTouchedBioquestions] = React.useState({});

  const title = props.title;
  const oldBioQuestions = props.bioQuestions;
  const saveMultipleQuestions = props.saveMultipleQuestions;
  const caregiverBackend = props.caregiverBackend;
  const Icon = props.icon;

  const resetQuestions = () => {
    setNewBioQuestions([...oldBioQuestions]);
    setTouchedBioquestions(Object.fromEntries(oldBioQuestions.map((question, idx) => [idx, false])));
  };

  // on load of bio questions, reset
  React.useEffect(() => {
    resetQuestions();
  }, [oldBioQuestions]);

  const setEditMode = () => {
    setMode('EDIT');
  };

  const setViewMode = () => {
    setMode('VIEW');
  };

  const save = async () => {
    setIsSaving(true);
    const questionUpdates = Object.fromEntries(
        Object.entries(touchedBioQuestions)
            .filter(([idx, touched]) => touched).map(([idx, touched]) => [newBioQuestions[idx].bioQuestionId, newBioQuestions[idx]]),
    );


    await saveMultipleQuestions(questionUpdates);
    setIsSaving(false);
    setViewMode();
  };

  const cancel = () => {
    resetQuestions();
    setViewMode();
  };

  const updateNewQuestionData = (bioQuestionId, questionData) => {
    setNewBioQuestions((prev) => prev.map((question, idx) => {
      // change specified question
      if (question.bioQuestionId === bioQuestionId) {
        // add to touched questions
        setTouchedBioquestions((prev) => (
          {
            ...prev,
            [idx]: true,
          }
        ));
        if (typeof questionData === 'function') {
          return {
            ...question,
            questionData: questionData(question.questionData),
          };
        } else {
          return {
            ...question,
            questionData,
          };
        }
      } else {
        return question;
      }
    }));
  };


  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
      onMouseOver={(e) => setMouseOver(true)}
      onMouseLeave={(e) => setMouseOver(false)}
      elevation={mode === 'EDIT' ? 10 : 1}
    >
      <Box
        sx={{
          display: 'flex',

          px: 2,
          py: 1,

          backgroundColor: 'grey.100',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          fontWeight='medium'
          variant='h5'
          sx={{

            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <Icon
            fontSize='inherit'
          />
          {title}
        </Typography>
        <IconButton
          sx={{
            visibility: mode === 'EDIT' ? 'hidden' : 'initial',
            opacity: mouseOver ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
          onClick={setEditMode}
        >
          <Edit/>
        </IconButton>
      </Box>

      <Stack
        sx={{
          padding: 2,
        }}
        spacing={mode === 'EDIT' ? 2 : 1}
      >
        {
          newBioQuestions.map((question, i) => (
            <BiographyQuestion
              key={question.bioQuestionId}
              mode={mode}
              bioQuestion={question}
              updateQuestionData={(questionData) => updateNewQuestionData(question.bioQuestionId, questionData)}
              caregiverBackend={caregiverBackend}
            />
          ))
        }
      </Stack>
      {
        mode === 'EDIT' && (
          <Stack
            direction='row'
            spacing={1}
            sx= {{
              justifyContent: 'flex-end',
              margin: 2,
            }}
          >
            <Button onClick={cancel} variant='outlined'>
              <Translate expression='generic.Cancel' />
            </Button>
            <Button
              loading={isSaving}
              onClick={save}
            >
              <Translate expression='generic.Save'/>
            </Button>
          </Stack>
        )
      }
    </Paper>
  );
};

BiographyQuestionGroup.propTypes = {
  title: PropTypes.string.isRequired,
  bioQuestions: PropTypes.array.isRequired,
  icon: PropTypes.elementType.isRequired,
  saveMultipleQuestions: PropTypes.func.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
};

export default BiographyQuestionGroup;
